import type { Router } from 'vue-router'
import { Capacitor } from '@capacitor/core'

const STORAGE_KEY = 'galidancia_last_activity_at'
/** 3 horas sin interacción → volver a Home (`/tabs/inicio`). */
export const IDLE_HOME_MS = 3 * 60 * 60 * 1000
export const HOME_PATH = '/tabs/inicio'

function isLogged(): boolean {
  return localStorage.getItem('token') !== null
}

function readLastActivity(): number {
  const raw = localStorage.getItem(STORAGE_KEY)
  const n = raw ? Number(raw) : 0
  return Number.isFinite(n) && n > 0 ? n : 0
}

export function touchAppActivity(): void {
  localStorage.setItem(STORAGE_KEY, String(Date.now()))
}

function isAlreadyHome(path: string): boolean {
  return path === HOME_PATH || path.startsWith(`${HOME_PATH}/`)
}

function shouldSkipIdleRedirect(path: string): boolean {
  if (isAlreadyHome(path)) return true
  if (path.startsWith('/login')) return true
  if (path.startsWith('/password-recovery')) return true
  if (path.startsWith('/pago-rapido')) return true
  return false
}

/**
 * Si lleva más de 3 h sin interactuar, redirige a Home.
 * Devuelve true si redirigió.
 */
export function redirectHomeIfIdle(router: Router): boolean {
  if (!isLogged()) return false

  const last = readLastActivity()
  if (!last) {
    touchAppActivity()
    return false
  }

  if (Date.now() - last < IDLE_HOME_MS) return false

  touchAppActivity()
  const path = router.currentRoute.value.path || ''
  if (shouldSkipIdleRedirect(path)) return false

  void router.replace(HOME_PATH)
  return true
}

/**
 * Escucha interacción / vuelta a primer plano y aplica la regla de idle → Home.
 */
export function setupIdleHomeRedirect(router: Router): () => void {
  const onActivity = () => {
    if (!isLogged()) return
    touchAppActivity()
  }

  const onResume = () => {
    if (!isLogged()) return
    if (!redirectHomeIfIdle(router)) {
      touchAppActivity()
    }
  }

  const activityEvents: Array<keyof DocumentEventMap> = [
    'pointerdown',
    'keydown',
    'touchstart',
    'scroll',
  ]

  for (const ev of activityEvents) {
    document.addEventListener(ev, onActivity, { capture: true, passive: true })
  }

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      onResume()
    } else {
      // Al salir a segundo plano dejamos la marca de la última interacción.
      onActivity()
    }
  }
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('focus', onResume)

  // Primera evaluación al arrancar (p. ej. app reabierta tras horas).
  onResume()

  let removeAppListener: (() => void) | undefined
  if (Capacitor.isNativePlatform()) {
    void import('@capacitor/app')
      .then(async ({ App }) => {
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) onResume()
          else onActivity()
        })
        removeAppListener = () => {
          void handle.remove()
        }
      })
      .catch(() => {
        /* plugin no disponible */
      })
  }

  // Por si la app se deja abierta en primer plano muchas horas.
  const intervalId = window.setInterval(() => {
    redirectHomeIfIdle(router)
  }, 60_000)

  return () => {
    for (const ev of activityEvents) {
      document.removeEventListener(ev, onActivity, true)
    }
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('focus', onResume)
    window.clearInterval(intervalId)
    removeAppListener?.()
  }
}
